import { useEffect, useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { toast } from "sonner";
import { DatabaseBackup, Download, RotateCcw, Trash2, Upload, ShieldCheck } from "lucide-react";

const TIPE = {
  manual: { label: "Manual", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  upload: { label: "Dari File", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  sebelum_restore: { label: "Otomatis · Sebelum Restore", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sebelum_hapus: { label: "Otomatis · Sebelum Hapus", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(2)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const fmtDT = (v) => {
  if (!v) return "-";
  const d = new Date(String(v).includes("T") || String(v).endsWith("Z") ? v : String(v).replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function BackupPage() {
  const [items, setItems] = useState([]);
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {mode: 'restore'|'import', item?, file?}
  const [typed, setTyped] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/backups").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);

  const create = async () => {
    setBusy(true);
    try { await api.post("/backups", { catatan }); setCatatan(""); toast.success("Backup berhasil dibuat"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (it) => {
    try {
      const res = await fetch(`${API_BASE}/backups/${it.id}/download`, { headers: { Authorization: `Bearer ${localStorage.getItem("khn_token")}` } });
      if (!res.ok) throw new Error("Gagal mengunduh backup");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob()); a.download = it.filename || "backup.json"; a.click();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (it) => {
    if (!window.confirm("Hapus file backup ini dari riwayat? File yang sudah di-download tetap aman.")) return;
    try { await api.delete(`/backups/${it.id}`); toast.success("Backup dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 30 * 1024 * 1024) { toast.error("Ukuran file backup maksimal 30 MB"); return; }
    setTyped(""); setConfirm({ mode: "import", file: f });
  };

  const expected = "RESTORE";
  const runConfirm = async () => {
    if (typed.trim().toUpperCase() !== expected) { toast.error(`Ketik ${expected} untuk konfirmasi`); return; }
    setBusy(true);
    try {
      if (confirm.mode === "restore") {
        const r = await api.post(`/backups/${confirm.item.id}/restore`, { konfirmasi: typed });
        toast.success(`Restore berhasil (${Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0)} data dipulihkan)`);
      } else {
        const fd = new FormData();
        fd.append("file", confirm.file); fd.append("konfirmasi", typed);
        const r = await api.post("/backups/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Restore dari file berhasil (${Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0)} data dipulihkan)`);
      }
      setConfirm(null); setTyped(""); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Backup & Restore" subtitle="Cadangkan seluruh data workspace, download file backup, dan pulihkan kapan saja bila terjadi error atau input sembarangan." />
      <div className="p-4 md:p-8 space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card-flat p-5 space-y-3" data-enter-scope data-testid="backup-create-card">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><DatabaseBackup size={18} className="brand-text" /> Buat Backup Baru</div>
            <p className="text-xs text-slate-500">Menyimpan semua data workspace ini (pemasukan, pengeluaran, kategori, pembeli, produksi, utang-piutang, pajak, pengaturan, dll). Akun login user tidak ikut di-backup.</p>
            <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Input value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="mis. Sebelum tutup buku 2026" data-testid="backup-catatan" /></div>
            <Button onClick={create} disabled={busy} className="brand-btn rounded-sm" data-enter-submit data-testid="backup-create-btn"><DatabaseBackup size={14} className="mr-1" /> {busy ? "Memproses..." : "Buat Backup Sekarang"}</Button>
          </div>
          <div className="card-flat p-5 space-y-3" data-testid="backup-import-card">
            <div className="flex items-center gap-2 font-semibold text-slate-900"><Upload size={18} className="brand-text" /> Restore dari File Backup</div>
            <p className="text-xs text-slate-500">Upload file <b>.json</b> hasil download backup workspace ini. <b className="text-rose-600">Seluruh data saat ini akan diganti</b> dengan isi file. Sistem otomatis membuat backup pengaman sebelum restore.</p>
            <input ref={fileRef} type="file" accept=".json,.gz,application/json" className="hidden" onChange={onPickFile} data-testid="backup-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-sm" data-testid="backup-import-btn"><Upload size={14} className="mr-1" /> Pilih File Backup...</Button>
          </div>
        </div>

        <div className="card-flat overflow-hidden">
          <div className="p-4 md:p-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Riwayat Backup</h3>
              <p className="text-xs text-slate-500">Disimpan per workspace (maks. 30 terbaru). Download untuk menyimpan salinan di komputer Anda.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm" data-testid="backup-table">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="p-3">Waktu</th><th className="p-3">Jenis</th><th className="p-3">Catatan</th>
                <th className="p-3 text-right">Jumlah Data</th><th className="p-3 text-right">Ukuran</th><th className="p-3">Oleh</th><th className="p-3 text-right">Aksi</th>
              </tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Belum ada backup. Klik tombol <b>Buat Backup Sekarang</b>.</td></tr>}
                {paged.map((it) => {
                  const t = TIPE[it.tipe] || { label: it.tipe, cls: "bg-slate-50 text-slate-700 border-slate-200" };
                  return (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`backup-row-${it.id}`}>
                      <td className="p-3 whitespace-nowrap">{fmtDT(it.created_at)}</td>
                      <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-sm border whitespace-nowrap ${t.cls}`}>{t.label}</span></td>
                      <td className="p-3 text-slate-600 max-w-[240px] truncate" title={it.catatan}>{it.catatan || "-"}</td>
                      <td className="p-3 text-right font-mono">{Number(it.total_rows || 0).toLocaleString("id-ID")}</td>
                      <td className="p-3 text-right font-mono text-slate-500">{fmtSize(it.size_bytes)}</td>
                      <td className="p-3 text-slate-500 text-[12px]">{it.created_by || "-"}</td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => download(it)} title="Download" className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-600" data-testid={`backup-download-${it.id}`}><Download size={15} /></button>
                          <button onClick={() => { setTyped(""); setConfirm({ mode: "restore", item: it }); }} title="Restore" className="p-1.5 rounded-sm hover:bg-amber-50 text-amber-700" data-testid={`backup-restore-${it.id}`}><RotateCcw size={15} /></button>
                          <button onClick={() => del(it)} title="Hapus" className="p-1.5 rounded-sm hover:bg-rose-50 text-rose-600" data-testid={`backup-delete-${it.id}`}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="backup-pagination" />
        </div>

      </div>

      <Dialog open={!!confirm} onOpenChange={(v) => { if (!v && !busy) setConfirm(null); }}>
        <DialogContent data-testid="backup-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Restore Data?</DialogTitle>
            <DialogDescription className="sr-only">Konfirmasi tindakan backup</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {confirm?.mode === "restore" && <p>Data workspace saat ini akan <b>diganti</b> dengan backup tanggal <b>{fmtDT(confirm.item?.created_at)}</b> ({Number(confirm.item?.total_rows || 0).toLocaleString("id-ID")} data).</p>}
            {confirm?.mode === "import" && <p>Data workspace saat ini akan <b>diganti</b> dengan isi file <b>{confirm.file?.name}</b>.</p>}
            <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm p-2.5">
              <ShieldCheck size={14} className="shrink-0 mt-0.5" /> Backup pengaman otomatis dibuat sebelum proses, jadi data saat ini masih bisa dikembalikan.
            </div>
            <div className="space-y-1.5">
              <Label>Ketik <b>{expected}</b> untuk melanjutkan</Label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={expected} autoFocus data-testid="backup-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} className="rounded-sm">Batal</Button>
            <Button onClick={runConfirm} disabled={busy || typed.trim().toUpperCase() !== expected}
              className="rounded-sm brand-btn" data-testid="backup-confirm-btn">
              {busy ? "Memproses..." : "Ya, Restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
